import React, { useEffect, useState, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Paper,
  Stack, Typography, Divider, IconButton, Tooltip, useMediaQuery, LinearProgress,
  MenuItem, TextField
} from '@mui/material';
import {
  Refresh, ReportProblem, CheckCircle, PhotoCamera, History, EventNote,
  TaskAlt, PendingActions, Image as ImageIcon
} from '@mui/icons-material';
import api from '../../api';
import { toProtectedFileUrl } from '../../utils/secureFile';

// Palette netral, flat, tanpa gradien — mengikuti gaya dashboard monitoring
const token = {
  bg: '#f5f7fa',
  surface: '#ffffff',
  ink: '#1f2937',
  inkSoft: '#6b7280',
  line: '#e5e7eb',
  primary: '#1976d2',
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
};

const formatDate = (value) => value
  ? new Date(value).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
  : '-';

const formatDateTime = (value) => value
  ? new Date(value).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '-';

const statusColor = (status) =>
  status === 'Selesai' ? 'success'
    : status === 'Proses' ? 'info'
    : 'warning';

const eventLabel = (type) =>
  type === 'DIBUAT' ? 'Laporan dibuat'
    : type === 'DITINDAKLANJUTI' ? 'Ditindaklanjuti'
    : type === 'DISELESAIKAN' ? 'Diselesaikan'
    : type;

const eventColor = (type) =>
  type === 'DISELESAIKAN' ? token.success
    : type === 'DITINDAKLANJUTI' ? token.info
    : token.warning;

const fotoUrl = (filename) => (filename ? toProtectedFileUrl(`images/${filename}`) : null);

const StatCard = ({ icon, label, value, color = 'primary' }) => (
  <Paper sx={{ p: 2, bgcolor: token.surface, border: `1px solid ${token.line}`, height: '100%' }} elevation={0}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center',
        bgcolor: `${token[color] || token.primary}14`, color: token[color] || token.primary, flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ color: token.ink, lineHeight: 1.1 }}>{value}</Typography>
        <Typography variant="caption" sx={{ color: token.inkSoft }}>{label}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const TimelineItem = ({ log, isLast }) => (
  <Box sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
    {!isLast && (
      <Box sx={{ position: 'absolute', left: 11, top: 26, bottom: -6, width: 2, bgcolor: token.line }} />
    )}
    <Box sx={{
      width: 24, height: 24, borderRadius: '50%', flexShrink: 0, mt: 0.3, zIndex: 1,
      display: 'grid', placeItems: 'center',
      bgcolor: eventColor(log.eventType), color: '#fff',
    }}>
      {log.eventType === 'DISELESAIKAN' ? <CheckCircle sx={{ fontSize: 14 }} />
        : log.eventType === 'DITINDAKLANJUTI' ? <TaskAlt sx={{ fontSize: 14 }} />
        : <ReportProblem sx={{ fontSize: 14 }} />}
    </Box>
    <Box sx={{ flex: 1, pb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" fontWeight={700} sx={{ color: token.ink }}>
          {eventLabel(log.eventType)}
        </Typography>
        {log.Actor && (
          <Chip size="small" variant="outlined" label={`${log.Actor.username} (${log.Actor.role})`} sx={{ fontSize: 10 }} />
        )}
      </Stack>
      <Typography variant="caption" sx={{ color: token.inkSoft, display: 'block', mt: 0.25 }}>
        <EventNote sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
        {formatDateTime(log.createdAt)}
      </Typography>
      {log.catatan && (
        <Typography variant="body2" sx={{ color: token.ink, mt: 0.75, bgcolor: token.bg, p: 1.25, borderRadius: 1.5 }}>
          {log.catatan}
        </Typography>
      )}
      {log.foto && (
        <Box sx={{ mt: 1 }}>
          <img
            src={fotoUrl(log.foto)} alt="Foto tindak lanjut"
            style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: `1px solid ${token.line}`, cursor: 'pointer' }}
            onClick={() => openInAppFilePreview({ url: fotoUrl(log.foto), title: eventLabel(log.eventType) })}
          />
        </Box>
      )}
    </Box>
  </Box>
);

const KeluhanCard = ({ item }) => {
  const logs = [...(item.Logs || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const followUps = logs.filter(l => l.eventType !== 'DIBUAT');

  return (
    <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${token.line}`, bgcolor: token.surface }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: token.ink }}>
              {item.BantuanLangkahLog?.tahap || 'Tahapan bantuan'}
            </Typography>
            <Typography variant="caption" sx={{ color: token.inkSoft }}>
              Dilaporkan {formatDateTime(item.createdAt)}
            </Typography>
          </Box>
          <Chip size="small" color={statusColor(item.status)} label={item.status} sx={{ fontWeight: 600 }} />
        </Stack>

        <Typography variant="body2" sx={{ color: token.ink, mt: 1.5 }}>
          {item.keluhan}
        </Typography>

        {item.bukti && (
          <Button size="small" startIcon={<ImageIcon />} sx={{ mt: 1, textTransform: 'none' }}
            onClick={() => openInAppFilePreview({ url: fotoUrl(item.bukti), title: 'Bukti keluhan' })}>
            Lihat Bukti Foto
          </Button>
        )}

        {item.status === 'Selesai' && item.resolvedAt && (
          <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }} icon={<CheckCircle fontSize="small" />}>
            <Typography variant="caption" fontWeight={600}>
              Selesai ditangani pada {formatDate(item.resolvedAt)}
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" fontWeight={700} sx={{ color: token.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <History sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
          Riwayat Tindak Lanjut ({followUps.length})
        </Typography>
        {followUps.length === 0 ? (
          <Typography variant="body2" sx={{ color: token.inkSoft, mt: 1 }}>
            Belum ada tindak lanjut. Laporan Anda sedang menunggu penanganan.
          </Typography>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            {followUps.map((log, idx) => (
              <TimelineItem key={log.id} log={log} isLast={idx === followUps.length - 1} />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const PenerimaTindakLanjut = () => {
  const isMobile = useMediaQuery('(max-width:800px)');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    setError('');
    try {
      const params = { limit: 100 };
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/keluhan', { params });
      setList(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal memuat data tindak lanjut.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const total = list.length;
  const selesai = list.filter(k => k.status === 'Selesai').length;
  const proses = list.filter(k => k.status === 'Proses').length;
  const belum = list.filter(k => k.status === 'Belum Di Tindak Lanjut').length;
  const pct = total > 0 ? Math.round((selesai / total) * 100) : 0;

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: token.bg, minHeight: '100vh' }}>
        {/* Header — flat, tidak mencolok */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600} sx={{ color: token.ink }}>
              <TaskAlt sx={{ mr: 1, verticalAlign: 'middle', color: token.primary }} />
              Monitoring Tindak Lanjut
            </Typography>
            <Typography variant="body2" sx={{ color: token.inkSoft }}>
              Pantau penanganan laporan keluhan Anda beserta foto dan tanggal tindak lanjut.
            </Typography>
          </Box>
          <Tooltip title="Perbarui data">
            <span>
              <IconButton onClick={() => fetchData(true)} disabled={refreshing || loading} size="small"
                sx={{ border: `1px solid ${token.line}`, bgcolor: token.surface }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {/* Statistik ringkas */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1.5, mb: 2,
        }}>
          <StatCard icon={<ReportProblem />} label="Total Laporan" value={total} color="primary" />
          <StatCard icon={<PendingActions />} label="Belum Ditangani" value={belum} color="warning" />
          <StatCard icon={<TaskAlt />} label="Sedang Diproses" value={proses} color="info" />
          <StatCard icon={<CheckCircle />} label="Selesai" value={selesai} color="success" />
        </Box>

        {/* Progress penyelesaian */}
        <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2, mb: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: token.ink }}>Penyelesaian Laporan</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: token.success }}>{pct}%</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={pct} color="success"
              sx={{ height: 8, borderRadius: 4, bgcolor: token.line }} />
            <Typography variant="caption" sx={{ color: token.inkSoft }}>
              {selesai} dari {total} laporan selesai ditangani
            </Typography>
          </CardContent>
        </Card>

        {/* Filter status */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField select size="small" label="Filter Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 220, bgcolor: token.surface }}>
            <MenuItem value="">Semua Status</MenuItem>
            <MenuItem value="Belum Di Tindak Lanjut">Belum Di Tindak Lanjut</MenuItem>
            <MenuItem value="Proses">Proses</MenuItem>
            <MenuItem value="Selesai">Selesai</MenuItem>
          </TextField>
        </Stack>

        {/* Daftar keluhan + timeline tindak lanjut */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchData()}>Coba Lagi</Button>}>
            {error}
          </Alert>
        ) : list.length === 0 ? (
          <Card elevation={0} sx={{ border: `1px solid ${token.line}`, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <ReportProblem sx={{ fontSize: 40, color: token.line, mb: 1 }} />
              <Typography variant="body1" fontWeight={600} sx={{ color: token.ink }}>Belum ada laporan</Typography>
              <Typography variant="body2" sx={{ color: token.inkSoft }}>
                Anda belum pernah membuat laporan keluhan. Laporan dapat dibuat dari halaman Dashboard.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {list.map((item) => <KeluhanCard key={item.id} item={item} />)}
          </Stack>
        )}

        {/* Hint foto dokumentasi tindak lanjut */}
        {!loading && !error && list.some(k => (k.Logs || []).some(l => l.foto)) && (
          <Alert severity="info" icon={<PhotoCamera fontSize="small" />} sx={{ mt: 2 }}>
            Klik foto pada riwayat tindak lanjut untuk melihat dokumentasi penanganan dalam ukuran penuh.
          </Alert>
        )}
      </Box>
    </div>
  );
};

export default PenerimaTindakLanjut;
